import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Eye, EyeOff, Clock, User, Calendar, Check } from 'lucide-react';
import Swal from 'sweetalert2';
import { checkEmailAvailability, checkPhoneAvailability, registerFirebaseUser } from '../services/FirebaseService';
import { checkSocialOutletPhone, createSocialOutletLead, createDaisUserFromLead, sendSocialOutletWelcome, fetchSocialAssociations, checkAssociationCode } from '../services/outletApi';
import { fetchOutletSlots, flattenSlots, formatDateLabel, formatTime } from '../services/slotsService';
import {
  todayISO, isPastDateISO, slotKey, earliestSlot, toInterestedPayload,
  splitFullName, maskBirthDate, birthMaskToISO, upcomingDatesISO,
  buildAssociationPayload, ASSOC_OTHER, ASSOC_NONE,
} from '../utils/queueFormUtils';
import WhatsappVerifyModal from './WhatsappVerifyModal';
import AlreadyRegisteredModal from './AlreadyRegisteredModal';

// Límites de la sección "horarios que podrían abrirse".
const MAX_INTERESTED_SLOTS = 3;
const VISIBLE_UPCOMING_COLLAPSED = 8;
// Chips de "qué día te gustaría" (fila virtual).
const TENTATIVE_DAY_CHIPS = 7;

const initialFormData = {
  nombre: '', // nombre completo; se divide en name/lastname al enviar
  email: '',
  password: '',
  telefono: '',
  fechaNacimiento: '', // máscara dd/mm/aaaa
  sexo: 'femenino',
  pronombre: 'ella',
  asociacionSel: '', // id de asociación (string) | ASSOC_OTHER | ASSOC_NONE
  asociacionOtra: '', // texto libre: "otra organización" o fallback sin catálogo
  codigoAsociacion: '',
  sinCodigo: false,
  fechaTentativa: '',
  aceptaTerminos: false,
};

// Etiqueta corta para los chips de día: Hoy / Mañana / "jue 6".
const chipDayLabel = (iso) => {
  const [y, mo, d] = iso.split('-').map(Number);
  const date = new Date(y, mo - 1, d);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  if (date.toDateString() === today.toDateString()) return 'Hoy';
  if (date.toDateString() === tomorrow.toDateString()) return 'Mañana';
  return date.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric' });
};

export default function RegistrationForm({ onBack, onSubmit, selectedSlot, selectedSpecialty }) {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Catálogo de asociaciones aliadas (dropdown). Si el fetch falla o viene
  // vacío, el campo degrada al texto libre de siempre.
  const [associations, setAssociations] = useState([]);
  const [assocLoading, setAssocLoading] = useState(true);
  // null = sin validar | 'checking' | 'valid' | 'invalid'
  const [codeStatus, setCodeStatus] = useState(null);

  // Horarios "que se abrirán" para marcar interés (solo modo fila virtual).
  const [upcoming, setUpcoming] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(!selectedSlot);
  const [interestedKeys, setInterestedKeys] = useState(new Set());
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  // "Otro día" expande el date picker para fechas fuera de los chips.
  const [showOtherDay, setShowOtherDay] = useState(false);
  // Contexto del gate de WhatsApp tras crear lead + dais.user.
  const [verifyCtx, setVerifyCtx] = useState(null); // { userId, authToken, phone }
  // Modal "ya tienes cuenta" cuando el tel/correo es de un dais.user activo.
  const [alreadyRegistered, setAlreadyRegistered] = useState(null); // { hasConsultations }
  const pendingSubmitRef = useRef(null);
  // Cuenta Firebase ya creada en un intento anterior de ESTE form (para no
  // chocar con email-already-in-use al reintentar tras un error posterior).
  const firebaseAuthRef = useRef(null); // { email, firebaseUid, authToken }

  // Use either selectedSlot.specialty or the separate selectedSpecialty
  const displaySpecialty = selectedSlot?.specialty || selectedSpecialty;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error on change
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  useEffect(() => {
    if (selectedSlot) return undefined;
    let alive = true;
    fetchOutletSlots().then(({ upcoming: apiUpcoming }) => {
      if (alive) {
        setUpcoming(flattenSlots(apiUpcoming, false));
        setUpcomingLoading(false);
      }
    });
    return () => { alive = false; };
  }, [selectedSlot]);

  useEffect(() => {
    let alive = true;
    fetchSocialAssociations().then((list) => {
      if (alive) {
        setAssociations(list);
        setAssocLoading(false);
      }
    });
    return () => { alive = false; };
  }, []);

  // Solo horarios de la especialidad elegida; si no hay especialidad, todos.
  // Dedupe por fecha+hora: varias doctoras con el mismo horario = un solo chip.
  const seenTimes = new Set();
  const relevantUpcoming = upcoming
    .filter((s) => !displaySpecialty?.id || s.specialty.id === displaySpecialty.id)
    .filter((s) => {
      const k = `${s.date}-${s.init}`;
      if (seenTimes.has(k)) return false;
      seenTimes.add(k);
      return true;
    });
  const interestedSlots = relevantUpcoming.filter((s) => interestedKeys.has(slotKey(s)));

  // Colapsado por default para que el form no se haga larguísimo.
  const visibleUpcoming = showAllUpcoming
    ? relevantUpcoming
    : relevantUpcoming.slice(0, VISIBLE_UPCOMING_COLLAPSED);
  const hiddenUpcomingCount = relevantUpcoming.length - visibleUpcoming.length;

  const upcomingByDate = {};
  visibleUpcoming.forEach((s) => {
    if (!upcomingByDate[s.date]) upcomingByDate[s.date] = [];
    upcomingByDate[s.date].push(s);
  });
  const upcomingDateKeys = Object.keys(upcomingByDate).sort();

  const atInterestCap = interestedKeys.size >= MAX_INTERESTED_SLOTS;

  const toggleInterest = (slot) => {
    const key = slotKey(slot);
    setInterestedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else if (next.size < MAX_INTERESTED_SLOTS) next.add(key);
      return next;
    });
    if (errors.fechaTentativa) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.fechaTentativa;
        return next;
      });
    }
  };

  const selectedAssociation = associations.find(
    (a) => String(a.id) === formData.asociacionSel,
  ) || null;

  const handleAssociationSelect = (value) => {
    handleChange('asociacionSel', value);
    setFormData((prev) => ({ ...prev, codigoAsociacion: '', sinCodigo: false }));
    setCodeStatus(null);
    if (errors.asociacionOtra) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.asociacionOtra;
        return next;
      });
    }
  };

  // Validación suave del código al salir del campo: avisa pero nunca
  // bloquea. Si no se pudo validar (red/deploy), no marcamos inválido.
  const handleCodeBlur = async () => {
    const code = formData.codigoAsociacion.trim();
    if (!code || !selectedAssociation) {
      setCodeStatus(null);
      return;
    }
    setCodeStatus('checking');
    const res = await checkAssociationCode(selectedAssociation.id, code);
    if (res.error) setCodeStatus(null);
    else setCodeStatus(res.valid ? 'valid' : 'invalid');
  };

  const validate = () => {
    const errs = {};
    if (!formData.nombre.trim()) errs.nombre = 'Requerido';
    if (!formData.email.trim()) {
      errs.email = 'Requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Correo no válido';
    }
    if (!formData.password) {
      errs.password = 'Requerido';
    } else if (formData.password.length < 8) {
      errs.password = 'Mínimo 8 caracteres';
    } else if (!/[a-zA-Z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
      errs.password = 'Debe tener al menos 1 letra y 1 número';
    }
    if (!formData.telefono.trim()) {
      errs.telefono = 'Requerido';
    } else if (!/^\d{10}$/.test(formData.telefono.replace(/\s/g, ''))) {
      errs.telefono = 'Deben ser 10 dígitos';
    }
    if (!formData.fechaNacimiento) {
      errs.fechaNacimiento = 'Requerido';
    } else if (!birthMaskToISO(formData.fechaNacimiento)) {
      errs.fechaNacimiento = 'Escribe una fecha válida (dd/mm/aaaa)';
    }
    if (!formData.sexo) errs.sexo = 'Requerido';
    if (!formData.pronombre) errs.pronombre = 'Requerido';
    if (associations.length > 0) {
      if (!formData.asociacionSel) {
        errs.asociacion = 'Elige una opción';
      } else if (formData.asociacionSel === ASSOC_OTHER && !formData.asociacionOtra.trim()) {
        errs.asociacionOtra = 'Cuéntanos de dónde nos conoces';
      }
    } else if (!formData.asociacionOtra.trim()) {
      errs.asociacion = 'Requerido';
    }
    // La fecha tentativa solo es requerida cuando NO hay ningún horario
    // próximo que marcar; con horarios listados es opcional.
    if (!selectedSlot && !upcomingLoading && relevantUpcoming.length === 0 && !formData.fechaTentativa) {
      errs.fechaTentativa = 'Elige el día que te gustaría tu consulta';
    } else if (!selectedSlot && formData.fechaTentativa && isPastDateISO(formData.fechaTentativa)) {
      errs.fechaTentativa = 'La fecha no puede ser anterior a hoy';
    }
    if (!formData.aceptaTerminos) errs.aceptaTerminos = 'Debes aceptar los términos';
    return errs;
  };

  const finishSubmit = (phoneVerified) => {
    setVerifyCtx(null);
    const data = pendingSubmitRef.current;
    if (!data) return;
    pendingSubmitRef.current = null; // evita doble onSubmit si se llamara dos veces
    // Solo en fila virtual y con número ya verificado: "recibimos tu solicitud".
    // Fire-and-forget, no bloquea la confirmación.
    if (phoneVerified && !selectedSlot && data.leadId) {
      sendSocialOutletWelcome(data.leadId);
    }
    Swal.fire({
      icon: 'success',
      title: '¡Listo!',
      text: selectedSlot ? 'Tu consulta ha sido reservada' : 'Te has unido a la fila virtual',
      confirmButtonColor: '#786ED9',
      timer: 1800,
      showConfirmButton: false,
    }).then(() => {
      onSubmit({ ...data, phoneVerified });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const firstErrorField = document.querySelector('.input-field.error, .select-field.error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setSubmitting(true);

    try {
      const email = formData.email.toLowerCase().trim();
      const phone10 = formData.telefono.replace(/\D/g, '');

      // 1. Cuenta existente en Odoo por teléfono O correo (dais.user activo).
      //    reason 'user' abre el modal de login; 'lead' conserva el inline.
      const phoneCheck = await checkSocialOutletPhone(phone10, email);
      if (!phoneCheck.available) {
        if (phoneCheck.reason === 'lead') {
          setErrors(prev => ({
            ...prev,
            telefono: 'Este teléfono ya está en la fila. Te avisaremos cuando haya un horario.',
          }));
        } else {
          setAlreadyRegistered({ hasConsultations: phoneCheck.hasConsultations });
        }
        return;
      }

      // 2. Correos huérfanos: existen en Firebase pero no tienen dais.user
      //    en Odoo (el backend no los ve). Error inline clásico.
      const emailAvailable = await checkEmailAvailability(email);
      if (!emailAvailable) {
        setErrors(prev => ({ ...prev, email: 'Este correo ya está registrado. Usa otro.' }));
        return;
      }
      if (phoneCheck.fallback) {
        const phoneAvailable = await checkPhoneAvailability('+52' + phone10);
        if (!phoneAvailable) {
          setErrors(prev => ({ ...prev, telefono: 'Este teléfono ya está registrado. Usa otro.' }));
          return;
        }
      }

      // 3. Cuenta Firebase Auth (reusar si ya se creó en un intento anterior)
      let fbAuth = firebaseAuthRef.current;
      if (!fbAuth || fbAuth.email !== email) {
        const reg = await registerFirebaseUser(email, formData.password);
        if (!reg.success) {
          if (reg.code === 'email-in-use') {
            setErrors(prev => ({ ...prev, email: reg.error }));
          } else {
            Swal.fire({ icon: 'error', title: 'Error', text: reg.error, confirmButtonColor: '#786ED9' });
          }
          return;
        }
        fbAuth = { email, firebaseUid: reg.firebaseUid, authToken: reg.authToken };
        firebaseAuthRef.current = fbAuth;
      }

      // 4. Crear lead (+ reservación con horarios de interés)
      const pronounMap = { 'ella': 1, 'el': 2, 'elle': 3 };
      const sexMap = { 'femenino': 'female', 'masculino': 'male', 'intersexual': 'intersex' };
      const nearest = earliestSlot(interestedSlots);
      const { name: firstName, lastname } = splitFullName(formData.nombre);

      const leadResult = await createSocialOutletLead({
        name: firstName,
        lastname,
        email,
        password: formData.password,
        phone: phone10,
        country_id: 156,
        country_code: '+52',
        pronoun_id: pronounMap[formData.pronombre] || 1,
        birth_date: birthMaskToISO(formData.fechaNacimiento),
        birth_sex: sexMap[formData.sexo] || 'female',
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City',
        utm_campaign: 'DaisiesSocial',
        utm_medium: 'Website',
        utm_source: 'social.daisies.mx',
        ...buildAssociationPayload(formData, associations),
        schedule_id: selectedSlot?.slotId || false,
        choosen_start_date: selectedSlot
          ? `${selectedSlot.date} ${selectedSlot.time}:00`
          : nearest
            ? `${nearest.date} ${nearest.init}:00`
            : (formData.fechaTentativa ? `${formData.fechaTentativa} 09:00:00` : false),
        professional_ids: selectedSlot?.professional?.id ? [selectedSlot.professional.id] : [],
        duration_id: displaySpecialty?.durationId || 5,
        specialty_id: displaySpecialty?.id,
        interested_slots: toInterestedPayload(interestedSlots),
      });

      if (!leadResult.success) {
        console.error('[handleSubmit] Lead error:', leadResult);
        Swal.fire({ icon: 'error', title: 'Error', text: 'Hubo un problema al registrar. Intenta de nuevo.', confirmButtonColor: '#786ED9' });
        return;
      }

      // 5. Crear dais.user desde el lead (habilita la verificación WhatsApp)
      const userResult = await createDaisUserFromLead({
        leadId: leadResult.lead_id,
        email,
        firebaseUid: fbAuth.firebaseUid,
      });

      pendingSubmitRef.current = {
        ...formData,
        nombre: firstName, // saludo en la confirmación
        nombreCompleto: formData.nombre,
        leadId: leadResult.lead_id,
        daisUserId: userResult.dais_user_id || null,
        countryId: 156,
        // Horarios marcados (doctora + hora) y/o día tentativo para mostrar
        // en la confirmación "estás esperando que se libere...".
        waitingSlots: interestedSlots,
        waitingDate: formData.fechaTentativa || null,
      };

      if (userResult.success && userResult.dais_user_id) {
        // 6. Gate de WhatsApp (no bloqueante: "Verificar después" disponible)
        setVerifyCtx({ userId: userResult.dais_user_id, authToken: fbAuth.authToken, phone: phone10 });
      } else {
        // Sin dais.user no hay OTP posible: completar sin verificación.
        console.warn('[handleSubmit] dais.user no creado:', userResult);
        finishSubmit(false);
      }
    } catch (error) {
      console.error('[handleSubmit] Error:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Error de conexión. Intenta más tarde.', confirmButtonColor: '#786ED9' });
    } finally {
      setSubmitting(false);
    }
  };

  // La fecha tentativa solo condiciona el submit cuando no hay horarios próximos.
  const needsTentativeDate = !selectedSlot && !upcomingLoading && relevantUpcoming.length === 0;
  const asociacionOk = associations.length > 0
    ? Boolean(formData.asociacionSel
        && (formData.asociacionSel !== ASSOC_OTHER || formData.asociacionOtra.trim()))
    : Boolean(formData.asociacionOtra.trim());
  const isFormValid = formData.nombre.trim() && formData.email && formData.password
    && formData.telefono && formData.fechaNacimiento.length === 10 && formData.sexo
    && formData.pronombre && asociacionOk && formData.aceptaTerminos
    && (!needsTentativeDate || formData.fechaTentativa);

  return (
    <div className="page-container" style={{ paddingBottom: 40 }}>
      {/* Back button */}
      <button className="back-button animate-fadeIn" onClick={onBack} id="back-to-landing">
        <ArrowLeft size={18} />
        Volver
      </button>

      <form onSubmit={handleSubmit} noValidate>
        {/* Header */}
        <div className="animate-fadeInUp" style={{ marginTop: 12, marginBottom: 24 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.5px' }}>
            Completa tus datos
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 4 }}>
            {selectedSlot ? 'Reserva tu consulta gratuita' : 'Únete a la fila virtual de ' + displaySpecialty?.name}
          </p>
        </div>

        {/* ─── Selected Slot/Specialty Summary ─── */}
        {displaySpecialty && (
          <div className="animate-fadeInUp-1 sp-selected-summary">
            <div className="sp-selected-summary-icon" style={{ background: displaySpecialty.color }}>
              <span>{displaySpecialty.emoji}</span>
            </div>
            <div className="sp-selected-summary-info">
              <strong>{displaySpecialty.name}</strong>
              {selectedSlot ? (
                <span className="sp-selected-summary-time">
                  <Clock size={12} />
                  {formatTime(selectedSlot.time)} → {formatTime(selectedSlot.timeEnd)}
                </span>
              ) : (
                <span className="sp-selected-summary-time">
                  <Clock size={12} />
                  Fila virtual (sin horario asignado)
                </span>
              )}
              {selectedSlot?.professional && (
                <span className="sp-selected-summary-doctor">
                  {selectedSlot.professional.photo ? (
                    <img src={selectedSlot.professional.photo} alt="" style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <User size={12} />
                  )}
                  {selectedSlot.professional.name}
                </span>
              )}
            </div>
            <button
              type="button"
              className="sp-selected-summary-change press-scale"
              onClick={onBack}
            >
              Cambiar
            </button>
          </div>
        )}

        {/* ─── Nombre completo ─── */}
        <div className="animate-fadeInUp-1" style={{ marginBottom: 16 }}>
          <label className="form-label" htmlFor="input-nombre">Nombre completo <span className="required">*</span></label>
          <input
            type="text"
            className={`input-field ${errors.nombre ? 'error' : ''}`}
            placeholder="María González"
            value={formData.nombre}
            onChange={(e) => handleChange('nombre', e.target.value)}
            id="input-nombre"
            autoComplete="name"
          />
          {errors.nombre && <p className="form-error">{errors.nombre}</p>}
        </div>

        {/* ─── Horarios que podrían abrirse (multi-selección de interés) ─── */}
        {!selectedSlot && upcomingDateKeys.length > 0 && (
          <div className="animate-fadeInUp-1 interest-section">
            <label className="form-label" style={{ marginBottom: 2 }}>
              Horarios que podrían abrirse
            </label>
            <p className="form-hint" style={{ marginTop: 0 }}>
              Marca hasta {MAX_INTERESTED_SLOTS} y te avisamos si se liberan
              {interestedKeys.size > 0 ? ` (${interestedKeys.size} de ${MAX_INTERESTED_SLOTS})` : ''}.
            </p>
            {upcomingDateKeys.map(date => (
              <div key={date}>
                <div className="interest-day-label">{formatDateLabel(date)}</div>
                <div className="sp-chip-row">
                  {upcomingByDate[date].map(slot => {
                    const selected = interestedKeys.has(slotKey(slot));
                    return (
                      <button
                        type="button"
                        key={slotKey(slot)}
                        className={`interest-chip ${selected ? 'interest-chip--selected' : ''}`}
                        style={selected ? undefined : { color: slot.specialty.accent, background: slot.specialty.color }}
                        onClick={() => toggleInterest(slot)}
                        disabled={!selected && atInterestCap}
                        title={`${slot.specialty.name} · ${slot.professional.name}`}
                      >
                        {selected && <Check size={13} />}
                        <span>{slot.specialty.emoji}</span>
                        <span>{formatTime(slot.init)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {(hiddenUpcomingCount > 0 || showAllUpcoming) && (
              <button
                type="button"
                className="interest-more press-scale"
                onClick={() => setShowAllUpcoming(v => !v)}
              >
                {showAllUpcoming ? 'Ver menos' : `Ver más horarios (${hiddenUpcomingCount})`}
              </button>
            )}
          </div>
        )}

        {/* ─── Fecha tentativa (solo fila virtual): chips de día, opcional si hay horarios ─── */}
        {!selectedSlot && (
          <div className="animate-fadeInUp-1" style={{ marginBottom: 16 }}>
            <label className="form-label">
              <Calendar size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />
              {relevantUpcoming.length > 0
                ? <>¿Prefieres otro día? Dinos cuál <span className="optional">(opcional)</span></>
                : <>¿Qué día te gustaría tu consulta? <span className="required">*</span></>}
            </label>
            <div className="day-chip-row" role="group" aria-label="Día tentativo">
              {upcomingDatesISO(TENTATIVE_DAY_CHIPS).map((iso) => {
                const selected = formData.fechaTentativa === iso;
                return (
                  <button
                    type="button"
                    key={iso}
                    className={`day-chip ${selected ? 'day-chip--selected' : ''}`}
                    aria-pressed={selected}
                    onClick={() => {
                      handleChange('fechaTentativa', selected ? '' : iso);
                      setShowOtherDay(false);
                    }}
                  >
                    {selected && <Check size={13} />}
                    <span className="day-chip-date">{chipDayLabel(iso)}</span>
                  </button>
                );
              })}
              <button
                type="button"
                className={`day-chip ${showOtherDay ? 'day-chip--selected' : ''}`}
                aria-expanded={showOtherDay}
                onClick={() => {
                  setShowOtherDay((v) => {
                    if (v) handleChange('fechaTentativa', '');
                    return !v;
                  });
                }}
              >
                Otro día
              </button>
            </div>
            {showOtherDay && (
              <input
                type="date"
                className={`input-field ${errors.fechaTentativa ? 'error' : ''}`}
                style={{ marginTop: 8 }}
                value={formData.fechaTentativa}
                onChange={(e) => handleChange('fechaTentativa', e.target.value)}
                id="input-fecha-tentativa"
                min={todayISO()}
                autoFocus
              />
            )}
            {formData.fechaTentativa && (
              <p className="form-hint">Te avisaremos por WhatsApp y la app cuando se libere un horario ese día</p>
            )}
            {errors.fechaTentativa && <p className="form-error">{errors.fechaTentativa}</p>}
          </div>
        )}

        {/* ─── Comunidad o Asociación: el dropdown atribuye, el código verifica ─── */}
        <div className="animate-fadeInUp-2" style={{ marginBottom: 16 }}>
          <label className="form-label" htmlFor={associations.length > 0 || assocLoading ? 'select-asociacion' : 'input-asociacion'}>
            ¿Vienes de parte de una comunidad o asociación? <span className="required">*</span>
          </label>
          {assocLoading ? (
            <select className="select-field" disabled id="select-asociacion">
              <option>Cargando opciones...</option>
            </select>
          ) : associations.length > 0 ? (
            <>
              <select
                className={`select-field ${errors.asociacion ? 'error' : ''}`}
                value={formData.asociacionSel}
                onChange={(e) => handleAssociationSelect(e.target.value)}
                id="select-asociacion"
              >
                <option value="">Selecciona una opción</option>
                {associations.map((a) => (
                  <option key={a.id} value={String(a.id)}>{a.name}</option>
                ))}
                <option value={ASSOC_OTHER}>Otra organización</option>
                <option value={ASSOC_NONE}>Vengo por mi cuenta</option>
              </select>
              {errors.asociacion && <p className="form-error">{errors.asociacion}</p>}

              {selectedAssociation && !formData.sinCodigo && (
                <div style={{ marginTop: 8 }}>
                  <input
                    type="text"
                    className={`input-field ${codeStatus === 'invalid' ? 'error' : ''}`}
                    placeholder={`Código de ${selectedAssociation.name}`}
                    value={formData.codigoAsociacion}
                    onChange={(e) => {
                      handleChange('codigoAsociacion', e.target.value.toUpperCase());
                      setCodeStatus(null);
                    }}
                    onBlur={handleCodeBlur}
                    id="input-codigo-asociacion"
                    autoComplete="off"
                  />
                  {codeStatus === 'checking' && <p className="form-hint">Validando código...</p>}
                  {codeStatus === 'valid' && (
                    <p className="form-hint" style={{ color: 'var(--daisies)', fontWeight: 600 }}>
                      Código verificado
                    </p>
                  )}
                  {codeStatus === 'invalid' && (
                    <p className="form-error">Ese código no coincide. Revísalo o continúa sin código.</p>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      handleChange('sinCodigo', true);
                      handleChange('codigoAsociacion', '');
                      setCodeStatus(null);
                    }}
                    id="btn-sin-codigo"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      marginTop: 6, fontSize: 13, color: 'var(--daisies)', fontWeight: 600,
                    }}
                  >
                    No tengo un código de asociación
                  </button>
                </div>
              )}
              {selectedAssociation && formData.sinCodigo && (
                <div style={{ marginTop: 8 }}>
                  <p className="form-hint">Sin problema, puedes continuar sin código.</p>
                  <button
                    type="button"
                    onClick={() => handleChange('sinCodigo', false)}
                    id="btn-tengo-codigo"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      marginTop: 2, fontSize: 13, color: 'var(--daisies)', fontWeight: 600,
                    }}
                  >
                    Sí tengo un código
                  </button>
                </div>
              )}

              {formData.asociacionSel === ASSOC_OTHER && (
                <div style={{ marginTop: 8 }}>
                  <input
                    type="text"
                    className={`input-field ${errors.asociacionOtra ? 'error' : ''}`}
                    placeholder="¿De dónde nos conoces?"
                    value={formData.asociacionOtra}
                    onChange={(e) => handleChange('asociacionOtra', e.target.value)}
                    id="input-asociacion-otra"
                  />
                  {errors.asociacionOtra && <p className="form-error">{errors.asociacionOtra}</p>}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Fallback: catálogo caído o vacío, texto libre como siempre */}
              <input
                type="text"
                className={`input-field ${errors.asociacion ? 'error' : ''}`}
                placeholder="Ej. Fundación X, Comunidad Y"
                value={formData.asociacionOtra}
                onChange={(e) => handleChange('asociacionOtra', e.target.value)}
                id="input-asociacion"
              />
              <p className="form-hint">Escribe el nombre de la organización a la que perteneces</p>
              {errors.asociacion && <p className="form-error">{errors.asociacion}</p>}
            </>
          )}
        </div>

        {/* ─── Email ─── */}
        <div className="animate-fadeInUp-2" style={{ marginBottom: 16 }}>
          <label className="form-label">Correo electrónico <span className="required">*</span></label>
          <input
            type="email"
            className={`input-field ${errors.email ? 'error' : ''}`}
            placeholder="tu@email.com"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            id="input-email"
          />
          {errors.email && <p className="form-error">{errors.email}</p>}
        </div>

        {/* ─── Password ─── */}
        <div className="animate-fadeInUp-2" style={{ marginBottom: 16 }}>
          <label className="form-label">Contraseña <span className="required">*</span></label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              className={`input-field ${errors.password ? 'error' : ''}`}
              placeholder="Mín. 8 caracteres, 1 letra y 1 número"
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              id="input-password"
              style={{ paddingRight: 48 }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: 14,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                padding: 4,
              }}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && <p className="form-error">{errors.password}</p>}
        </div>

        {/* ─── Phone ─── */}
        <div className="animate-fadeInUp-3" style={{ marginBottom: 16 }}>
          <label className="form-label">Teléfono <span className="required">*</span></label>
          <div className="phone-input-group">
            <div className="phone-prefix">
              <span className="flag">🇲🇽</span>
              <span>+52</span>
            </div>
            <input
              type="tel"
              className={`input-field ${errors.telefono ? 'error' : ''}`}
              placeholder="10 dígitos"
              value={formData.telefono}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                handleChange('telefono', val);
              }}
              id="input-telefono"
              inputMode="numeric"
            />
          </div>
          <p className="form-hint">País: México · Formato: +52__________</p>
          {errors.telefono && <p className="form-error">{errors.telefono}</p>}
        </div>

        {/* ─── Fecha de nacimiento: máscara dd/mm/aaaa (tecleo directo, sin calendario) ─── */}
        <div className="animate-fadeInUp-3" style={{ marginBottom: 16 }}>
          <label className="form-label" htmlFor="input-fecha">Fecha de nacimiento <span className="required">*</span></label>
          <input
            type="text"
            inputMode="numeric"
            className={`input-field ${errors.fechaNacimiento ? 'error' : ''}`}
            placeholder="dd/mm/aaaa"
            value={formData.fechaNacimiento}
            onChange={(e) => handleChange('fechaNacimiento', maskBirthDate(e.target.value))}
            id="input-fecha"
            maxLength={10}
          />
          {errors.fechaNacimiento && <p className="form-error">{errors.fechaNacimiento}</p>}
        </div>

        {/* ─── Sex & Pronoun ─── */}
        <div className="animate-fadeInUp-4 grid-2" style={{ marginBottom: 20 }}>
          <div>
            <label className="form-label">Sexo de nacimiento <span className="required">*</span></label>
            <select
              className={`select-field ${errors.sexo ? 'error' : ''}`}
              value={formData.sexo}
              onChange={(e) => handleChange('sexo', e.target.value)}
              id="select-sexo"
            >
              <option value="">Selecciona</option>
              <option value="femenino">Femenino</option>
              <option value="masculino">Masculino</option>
              <option value="intersexual">Intersexual</option>
            </select>
            {errors.sexo && <p className="form-error">{errors.sexo}</p>}
          </div>
          <div>
            <label className="form-label">Pronombre <span className="required">*</span></label>
            <select
              className={`select-field ${errors.pronombre ? 'error' : ''}`}
              value={formData.pronombre}
              onChange={(e) => handleChange('pronombre', e.target.value)}
              id="select-pronombre"
            >
              <option value="">Selecciona</option>
              <option value="ella">Ella</option>
              <option value="el">Él</option>
              <option value="elle">Elle</option>
            </select>
            {errors.pronombre && <p className="form-error">{errors.pronombre}</p>}
          </div>
        </div>

        {/* ─── Terms ─── */}
        <div className="animate-fadeInUp-4" style={{ marginBottom: 24 }}>
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={formData.aceptaTerminos}
              onChange={(e) => handleChange('aceptaTerminos', e.target.checked)}
              id="checkbox-terms"
            />
            <span style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.4 }}>
              Acepto los{' '}
              <a
                href="https://daisies.mx/terminos-y-condiciones"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--daisies)', textDecoration: 'none', fontWeight: 600 }}
              >
                términos y condiciones
              </a>
            </span>
          </label>
          {errors.aceptaTerminos && <p className="form-error" style={{ marginLeft: 30 }}>{errors.aceptaTerminos}</p>}
        </div>

        {/* ─── Submit ─── */}
        <div className="animate-fadeInUp-5">
          <button
            type="submit"
            className="btn-primary press-scale"
            disabled={!isFormValid || submitting}
            id="submit-registration"
            style={{
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {submitting ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="animate-gentlePulse">Registrando...</span>
              </span>
            ) : (
              selectedSlot ? 'Reservar Consulta Gratuita' : 'Unirme a la fila'
            )}
          </button>
        </div>
      </form>

      {verifyCtx && (
        <WhatsappVerifyModal
          userId={verifyCtx.userId}
          authToken={verifyCtx.authToken}
          phone={verifyCtx.phone}
          onVerified={() => finishSubmit(true)}
          onSkip={() => finishSubmit(false)}
        />
      )}
      {alreadyRegistered && (
        <AlreadyRegisteredModal
          hasConsultations={alreadyRegistered.hasConsultations}
          onClose={() => setAlreadyRegistered(null)}
        />
      )}
    </div>
  );
}
